import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { PropertyUnit } from "@/models/properties.model";
import { ReadAllResponse, uuid } from "@/interfaces";
import type { UnitForm } from "@/repositories/unit-records";
import {
    createPropertyUnit,
    deletePropertyUnit,
    searchUnits,
    updatePropertyUnit,
} from "@/app/pmc/properties-detail/properties-detail.components/property-units/property-units.api.ts";

interface PropertyUnitState {
    units: PropertyUnit[];
    total: number;
    loading: boolean;
    saving: boolean;
    deleting: boolean;
    error: string | null;
    filters: {
        unitNumber: string;
        page: number;
        pageSize: number;
        sortDirection: "asc" | "desc";
    };
}

const initialState: PropertyUnitState = {
    units: [],
    total: 0,
    loading: false,
    saving: false,
    deleting: false,
    error: null,
    filters: {
        unitNumber: "",
        page: 1,
        pageSize: 10,
        sortDirection: "asc",
    },
};

export const getUnits = createAsyncThunk(
    "propertyUnits/getAll",
    async (propertyId: uuid, { getState }) => {
        const state = getState() as { propertyUnits: PropertyUnitState };
        const { unitNumber, page, pageSize, sortDirection } = state.propertyUnits.filters;

        return await searchUnits(propertyId, unitNumber, page, pageSize, sortDirection);
    }
);

export const createUnit = createAsyncThunk(
    "propertyUnits/create",
    async (unit: UnitForm & { property: uuid }) => {
        await createPropertyUnit(unit);
        return unit.property;
    }
);

export const updateUnit = createAsyncThunk(
    "propertyUnits/update",
    async ({ propertyId, id, updates }: { propertyId: uuid; id: uuid; updates: UnitForm }) => {
        await updatePropertyUnit(propertyId, id, updates);
        return propertyId;
    }
);

export const deleteUnit = createAsyncThunk(
    "propertyUnits/delete",
    async ({ propertyId, id }: { propertyId: uuid; id: uuid }) => {
        await deletePropertyUnit(propertyId, id);
        return propertyId;
    }
);

const propertyUnitSlice = createSlice({
    name: "propertyUnits",
    initialState,
    reducers: {
        setPage(state, action: PayloadAction<number>) {
            state.filters.page = action.payload;
        },
        setPageSize(state, action: PayloadAction<number>) {
            state.filters.pageSize = action.payload;
            state.filters.page = 1;
        },
        setUnitSearch(state, action: PayloadAction<string>) {
            state.filters.unitNumber = action.payload;
            state.filters.page = 1;
        },
        resetFilters(state) {
            state.filters.unitNumber = "";
            state.filters.page = 1;
            state.filters.pageSize = 10;
            state.filters.sortDirection = "asc";
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getUnits.pending, (state) => {
                state.loading = true;
            })
            .addCase(getUnits.fulfilled, (state, action: PayloadAction<ReadAllResponse<PropertyUnit>>) => {
                state.units = action.payload.data;
                state.total = action.payload.count || 0;
                state.loading = false;
            })
            .addCase(getUnits.rejected, (state) => {
                state.loading = false;
            })
            .addCase(createUnit.pending, (state) => {
                state.saving = true;
            })
            .addCase(createUnit.fulfilled, (state) => {
                state.saving = false;
            })
            .addCase(updateUnit.pending, (state) => {
                state.saving = true;
            })
            .addCase(updateUnit.fulfilled, (state) => {
                state.saving = false;
            })
            .addCase(deleteUnit.pending, (state) => {
                state.deleting = true;
            })
            .addCase(deleteUnit.fulfilled, (state) => {
                state.deleting = false;
            })
            .addCase(deleteUnit.rejected, (state) => {
                state.deleting = false;
            });
    },
});

export const {
    setPage,
    setPageSize,
    setUnitSearch,
    resetFilters,
} = propertyUnitSlice.actions;

export default propertyUnitSlice.reducer;
